import React, { useState } from 'react';
import {
  Center,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { PiShareNetworkBold } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { PAYMENT_LINKS_ENDPOINTS } from '../links.constants';
import { BroxelRawRecord } from '../models/broxel-raw-payment-link';
import { PaymentLinkCreatedView } from './PaymentLinkCreatedView';

interface ShareLinkButtonProps {
  id: string;
}

interface BroxelRawByIdResponse {
  success: boolean;
  message: string;
  data: BroxelRawRecord;
}

/**
 * Ícono de "Compartir" de la columna Link en la Consulta. Al hacer clic, trae el link
 * por id (GET bff/payment-links-view/broxel/raw/:id — mismo id que ya viene en cada
 * registro del listado) y reutiliza `PaymentLinkCreatedView` (la misma pantalla que se
 * muestra al crear un link) dentro de un modal, mostrando el estado real del link.
 */
export const ShareLinkButton = ({ id }: ShareLinkButtonProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [record, setRecord] = useState<BroxelRawRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setIsOpen(true);
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<BroxelRawByIdResponse>(PAYMENT_LINKS_ENDPOINTS.broxelRawById(id));
      setRecord(response.data.data);
    } catch {
      setError('No se pudo cargar el link de pago, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setRecord(null);
    setError(null);
  };

  const handleGenerateAnother = () => {
    handleClose();
    navigate('/links/generacion');
  };

  return (
    <>
      <IconButton
        aria-label="Compartir"
        icon={<PiShareNetworkBold />}
        colorScheme="button-primary"
        variant="outline"
        borderRadius="full"
        size="sm"
        onClick={handleOpen}
      />

      <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent backgroundColor="transparent" boxShadow="none">
          <ModalCloseButton zIndex={1} />
          <ModalBody padding="0">
            {loading &&
              <Center backgroundColor="white" borderRadius="16px" padding="xxl">
                <Spinner />
              </Center>
            }

            {!loading && error &&
              <Center backgroundColor="white" borderRadius="16px" padding="xxl">
                <Text color="destructive.700">{error}</Text>
              </Center>
            }

            {!loading && !error && record &&
              <PaymentLinkCreatedView
                data={{
                  id: record.id,
                  token: record.token,
                  concept: record.concept,
                  amount: record.amount,
                  expire_date: record.expire_date,
                  // `url` la entrega Broxel tal cual (igual que en la creación) — el front
                  // no la arma. Si el BFF todavía no la agrega a este passthrough, queda
                  // vacía en vez de inventar un dominio del lado del cliente.
                  url: record.url ?? '',
                }}
                branchName={record.merchant?.name_merchant ?? ''}
                status={record.status_concept}
                primaryActionLabel="Generar Otro Link de Pago"
                onCreateAnother={handleGenerateAnother}
              />
            }
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
